// src/app/shared/markdown/code-highlight.directive.ts

import {
    AfterViewInit,
    Directive,
    ElementRef,
    Inject,
    NgZone,
    OnDestroy
  } from '@angular/core';
  import { isPlatformBrowser } from '@angular/common';
  import { PLATFORM_ID } from '@angular/core';
  
  import * as Prism from 'prismjs';
  import 'prismjs/components/prism-javascript';
  import 'prismjs/components/prism-typescript';
  import 'prismjs/components/prism-markup';
  import 'prismjs/components/prism-json';
  import 'prismjs/components/prism-bash';
  
  import { initPrismGenius } from './prism-genius';
  
  @Directive({
    selector: '[appCodeHighlight]'
  })
  export class CodeHighlightDirective implements AfterViewInit, OnDestroy {
    private observer: MutationObserver | null = null;
    private scheduled = false;
    private removeClickHandler: (() => void) | null = null;
  
    constructor(
      private host: ElementRef<HTMLElement>,
      private zone: NgZone,
      @Inject(PLATFORM_ID) private platformId: object
    ) {}
  
    ngAfterViewInit(): void {
      if (!isPlatformBrowser(this.platformId)) return;
  
      // Prism best practice for SPAs: manual highlighting
      (Prism as any).manual = true; // Prism supports manual mode :contentReference[oaicite:3]{index=3}
      initPrismGenius(Prism);
  
      this.zone.runOutsideAngular(() => {
        // 1) Initial pass
        this.decorateAndHighlight();
  
        // 2) Watch future markdown changes
        this.observer = new MutationObserver(() => this.schedule());
        this.observer.observe(this.host.nativeElement, { childList: true, subtree: true });
  
        // 3) One click handler for all “Copy” buttons (event delegation)
        const handler = (ev: Event) => this.onClick(ev);
        this.host.nativeElement.addEventListener('click', handler, true);
        this.removeClickHandler = () =>
          this.host.nativeElement.removeEventListener('click', handler, true);
      });
    }
  
    ngOnDestroy(): void {
      try {
        this.observer?.disconnect();
        this.observer = null;
      } catch {}
  
      try {
        this.removeClickHandler?.();
        this.removeClickHandler = null;
      } catch {}
    }
  
    private schedule(): void {
      if (this.scheduled) return;
      this.scheduled = true;
  
      requestAnimationFrame(() => {
        this.scheduled = false;
        this.decorateAndHighlight();
      });
    }
  
    private decorateAndHighlight(): void {
      const root = this.host.nativeElement;
  
      const codeNodes = Array.from(root.querySelectorAll('pre > code')) as HTMLElement[];
  
      for (const codeEl of codeNodes) {
        const pre = codeEl.parentElement as HTMLElement | null;
        if (!pre) continue;
  
        // Ensure Prism language class exists on both <pre> and <code>
        const lang = this.detectLanguage(codeEl) || this.detectLanguage(pre) || 'plaintext';
        this.ensureLanguageClasses(pre, codeEl, lang);
  
        // Wrap with header only once
        if (!pre.closest('.codeblock')) {
          this.wrapWithHeader(pre, lang);
        }
  
        // Prism highlight
        try {
          Prism.highlightElement(codeEl);
        } catch {
          // If Prism fails, do nothing — code still renders.
        }
      }
    }
  
    private detectLanguage(el: HTMLElement): string | null {
      const cls = el.className || '';
      const m = cls.match(/language-([a-z0-9-]+)/i);
      return m?.[1]?.toLowerCase() ?? null;
    }
  
    private ensureLanguageClasses(pre: HTMLElement, code: HTMLElement, lang: string): void {
      const normalized = lang.toLowerCase();
  
      // Prism expects language-xxx
      const preNeeds = !/language-/.test(pre.className);
      const codeNeeds = !/language-/.test(code.className);
  
      if (preNeeds) pre.classList.add(`language-${normalized}`);
      if (codeNeeds) code.classList.add(`language-${normalized}`);
  
      // Some markdown pipelines generate "lang-ts" or "ts"
      // Normalize common aliases
      if (normalized === 'ts') {
        pre.classList.remove('language-ts');
        code.classList.remove('language-ts');
        pre.classList.add('language-typescript');
        code.classList.add('language-typescript');
      }
      if (normalized === 'js') {
        pre.classList.remove('language-js');
        code.classList.remove('language-js');
        pre.classList.add('language-javascript');
        code.classList.add('language-javascript');
      }
    }
  
    private wrapWithHeader(pre: HTMLElement, lang: string): void {
      const wrapper = document.createElement('div');
      wrapper.className = 'codeblock';
  
      const bar = document.createElement('div');
      bar.className = 'codeblock__bar';
  
      const badge = document.createElement('span');
      badge.className = 'codeblock__lang';
      badge.textContent = (lang || 'code').toUpperCase();
  
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'codeblock__copy';
      btn.textContent = 'Copy';
  
      bar.appendChild(badge);
      bar.appendChild(btn);
  
      // Insert wrapper where <pre> was
      const parent = pre.parentElement;
      if (!parent) return;
  
      parent.insertBefore(wrapper, pre);
      wrapper.appendChild(bar);
      wrapper.appendChild(pre);
  
      // mark
      wrapper.setAttribute('data-codeblock', '1');
    }
  
    private async onClick(ev: Event): Promise<void> {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
  
      const btn = target.closest('.codeblock__copy') as HTMLButtonElement | null;
      if (!btn) return;
  
      const wrapper = btn.closest('.codeblock') as HTMLElement | null;
      if (!wrapper) return;
  
      const codeEl = wrapper.querySelector('pre > code') as HTMLElement | null;
      const text = codeEl?.innerText ?? '';
  
      if (!text.trim()) return;
  
      try {
        await navigator.clipboard.writeText(text);
        btn.textContent = 'Copied!';
        setTimeout(() => (btn.textContent = 'Copy'), 900);
      } catch {
        // fallback (older browsers)
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
          btn.textContent = 'Copied!';
          setTimeout(() => (btn.textContent = 'Copy'), 900);
        } catch {}
        ta.remove();
      }
    }
  }
  